import { useState } from 'react';
import {
  AppButton,
  AppFilePicker,
  AppFormDialog,
  AppMenuSheet,
  AppTextField,
  Icon,
} from '@core/designsystem';
import type { DocumentType } from '@core/domain/model';
import { DOCUMENT_TYPES, documentsCount, typeLabel } from './formatter';
import { titleOf } from './imports';

/**
 * Добавление документа: откуда взять, как назвать, каким типом пометить.
 *
 * Все источники системные, своих экранов съёмки нет: живой рамки в видоискателе
 * в вебе не будет ([WEB_ROADMAP.md](docs/WEB_ROADMAP.md)), а системная камера
 * не спрашивает разрешений и не переспрашивает их при каждом запуске.
 */

/** Что выбрал юрист: снять, взять из галереи или взять файл. */
export type AddSource = 'camera' | 'gallery' | 'files';

/**
 * Выбор типа документа списком.
 *
 * Четырнадцать значений, перечень утверждён владельцем ([D-24](docs/DECISIONS.md)):
 * новых в нём не появляется без согласования.
 */
export function TypePicker({
  selected,
  onSelect,
}: {
  selected: DocumentType;
  onSelect: (type: DocumentType) => void;
}) {
  return (
    <div className="type-picker">
      {DOCUMENT_TYPES.map((type) => (
        <button
          key={type}
          type="button"
          className="type-picker__item"
          aria-pressed={type === selected}
          onClick={() => onSelect(type)}
        >
          {typeLabel(type)}
        </button>
      ))}
    </div>
  );
}

/**
 * Название документа сразу после съёмки — ключевой шаг всего продукта.
 *
 * Без него в деле окажется та же папка с `IMG_3847`, ради ухода от которой приложение
 * и делается. Но в суде на это может не быть времени, поэтому «Потом» — полноценная
 * кнопка: документ получает пометку «Не разобрано» и видно, что к нему надо вернуться
 * ([D-29](docs/DECISIONS.md)).
 */
export function NameDocumentDialog({
  suggestion = '',
  onDismiss,
  onConfirm,
}: {
  /** Имя файла с устройства подставляется сразу: придумывать его заново незачем. */
  suggestion?: string;
  onDismiss: () => void;
  onConfirm: (title: string | null, type: DocumentType) => void;
}) {
  const [title, setTitle] = useState(suggestion);
  const [type, setType] = useState<DocumentType>('Other');
  /** Название, поставленное выбором типа. Набранное руками с ним не совпадёт. */
  const [fromType, setFromType] = useState('');

  /**
   * Выбор типа заодно называет документ.
   *
   * Юрист читает эти плитки как быстрые названия — и он прав: «Доверенность» и есть
   * название доверенности. Раньше выбор ставил только тип, поле оставалось пустым,
   * «Готово» — недоступным, и выглядело это поломкой: «тип он мне поставил,
   * но в название не вписал». Набранное руками название выбор типа не трогает.
   */
  const chooseType = (next: DocumentType) => {
    // «Без типа» — не название: подставлять его в поле незачем.
    const named = next === 'Other' ? '' : typeLabel(next);

    setType(next);
    setTitle((current) => (current.trim().length === 0 || current === fromType ? named : current));
    setFromType(named);
  };

  return (
    <AppFormDialog
      title="Название документа"
      onDismiss={() => {
        // «Отмена» здесь означает «положить без названия», а не «не класть»:
        // файл уже выбран, и терять его из-за незаполненного поля нельзя.
        onConfirm(null, type);
        onDismiss();
      }}
      dismissLabel="Потом"
      confirm={{
        label: 'Готово',
        enabled: title.trim().length > 0,
        onClick: () => {
          onConfirm(title, type);
          onDismiss();
        },
      }}
    >
      <AppTextField
        value={title}
        onChange={setTitle}
        label="Например, «Исковое заявление»"
        autoFocus
      />
      <p className="add-document__hint">Или выберите тип — он подставится в название</p>
      <TypePicker selected={type} onSelect={chooseType} />
      <p className="add-document__hint">
        Без названия документ попадёт в дело как «Не разобрано»
      </p>
    </AppFormDialog>
  );
}

/**
 * Несколько файлов сразу.
 *
 * Названия у пачки не спрашиваем — его не придумать для десяти файлов. Тип обычно
 * общий («это всё определения суда»), и один выбор избавляет от похода в каждый
 * документ потом. Для снимков главное другое: собрать их в один документ.
 */
export function BatchDialog({
  count,
  canMerge,
  onDismiss,
  onConfirm,
}: {
  count: number;
  /** Собрать в один PDF можно только снимки: чужой PDF мы на этом шаге не трогаем. */
  canMerge: boolean;
  onDismiss: () => void;
  onConfirm: (choice: { type: DocumentType; mergeIntoOne: boolean; title: string | null }) => void;
}) {
  const [type, setType] = useState<DocumentType>('Other');
  const [merge, setMerge] = useState(canMerge);
  const [title, setTitle] = useState('');

  return (
    <AppFormDialog
      title={`Добавляем ${documentsCount(count)}`}
      onDismiss={onDismiss}
      confirm={{
        label: 'Добавить',
        onClick: () => {
          onConfirm({ type, mergeIntoOne: canMerge && merge, title: title.trim() || null });
          onDismiss();
        },
      }}
    >
      {canMerge ? (
        <>
          <button
            type="button"
            className="add-document__switch"
            aria-pressed={merge}
            onClick={() => setMerge(!merge)}
          >
            <span className="add-document__switch-texts">
              <span>Один документ</span>
              <span className="add-document__hint">Снимки станут страницами одного файла</span>
            </span>
            <Icon name={merge ? 'check' : 'close'} size={22} />
          </button>

          {merge ? (
            <AppTextField
              value={title}
              onChange={setTitle}
              label="Название, например «Материалы дела»"
            />
          ) : null}
        </>
      ) : null}

      <p className="add-document__hint">
        Тип можно задать сразу для всех — потом он меняется у каждого
      </p>
      <TypePicker selected={type} onSelect={setType} />
    </AppFormDialog>
  );
}

/**
 * Постраничная съёмка: замена сканера там, где его нет.
 *
 * Системная камера отдаёт по одному кадру, поэтому после каждого спрашиваем — снять
 * следующую страницу или собрать документ. Закрыть окно мимо кнопок нельзя: снятые
 * страницы иначе некуда деть.
 */
export function PagedCameraDialog({
  pages,
  onShootMore,
  onFinish,
}: {
  pages: number;
  onShootMore: (photos: File[]) => void;
  onFinish: () => void;
}) {
  return (
    <div className="dialog-scrim" role="presentation">
      <div className="dialog" role="dialog" aria-modal="true" aria-label="Снято страниц">
        <h2 className="dialog__title">Снято страниц: {pages}</h2>
        <div className="dialog__body">
          <p className="add-document__hint">Снять следующую страницу или собрать документ?</p>
        </div>
        <div className="dialog__buttons">
          <AppButton variant="text" onClick={onFinish}>
            Готово
          </AppButton>
          <AppFilePicker
            label="Ещё страница"
            variant="text"
            accept="image/*"
            capture="environment"
            onFiles={onShootMore}
          />
        </div>
      </div>
    </div>
  );
}

/** Лист источников: три системных диалога и ничего своего. */
export function SourcesSheet({
  onDismiss,
  onPhotos,
  onFiles,
}: {
  onDismiss: () => void;
  /** Снимки: из камеры по одному кадру, из галереи — сколько выбрали. */
  onPhotos: (photos: File[], source: AddSource) => void;
  onFiles: (files: File[]) => void;
}) {
  return (
    <AppMenuSheet
      title="Добавить документ"
      onDismiss={onDismiss}
      items={[
        {
          icon: 'camera_alt',
          label: 'Снять камерой',
          onSelect: () => undefined,
          picker: {
            accept: 'image/*',
            capture: 'environment' as const,
            multiple: false,
            onFiles: (photos: File[]) => onPhotos(photos, 'camera'),
          },
        },
        {
          icon: 'photo_library',
          label: 'Из галереи',
          onSelect: () => undefined,
          picker: {
            accept: 'image/*',
            multiple: true,
            onFiles: (photos: File[]) => onPhotos(photos, 'gallery'),
          },
        },
        {
          icon: 'upload_file',
          label: 'Файл с устройства',
          onSelect: () => undefined,
          picker: {
            multiple: true,
            onFiles,
          },
        },
      ]}
    />
  );
}

/** Имя, предлагаемое диалогу для файла с устройства. */
export const suggestionFor = (file: File | undefined): string => (file ? titleOf(file.name) : '');
